import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Trash2, ChevronUp, ChevronDown, Eye, EyeOff,
  Save, Globe2, Upload, Loader2, RefreshCw, ImageIcon, Copy, X,
} from "lucide-react";
import { SECTION_DEFAULTS, SECTION_TYPE_LABELS } from "@/components/landing/LandingSection";

const API = process.env.REACT_APP_BACKEND_URL;
const SECTION_TYPES = Object.keys(SECTION_DEFAULTS);
const SECTION_TYPE_DESCRIPTIONS = {
  hero: "Banner principale a tutta schermata con titolo grande, sottotitolo e 2 pulsanti CTA. Tipicamente la prima sezione della pagina.",
  split: "Layout a 2 colonne: testo (titolo + corpo + statistiche) da un lato, immagine dall'altro. Posizione immagine configurabile.",
  feature_grid: "Sezione con titolo + paragrafo + griglia di card (con icona, titolo, descrizione). Ideale per elencare 3 feature.",
  cta: "Sezione finale con titolo emozionale, paragrafo e un grande pulsante. Spinge l'utente all'azione.",
};
const LANGS = [
  { id: "it", label: "Italiano" },
  { id: "en", label: "English" },
];
const ICON_OPTIONS = [
  "book", "sword", "swords", "trophy", "shop", "music",
  "mobile", "shield", "sparkles", "crown", "clock", "flag", "users",
];

// Deep-clone helper (handles only plain JSON, which is all we deal with here).
const clone = (o) => JSON.parse(JSON.stringify(o));

const newSection = (type) => ({
  id: crypto.randomUUID(),
  type,
  visible: true,
  content: {
    it: clone(SECTION_DEFAULTS[type]),
    en: clone(SECTION_DEFAULTS[type]),
  },
});

// ─── Inline image uploader: button that triggers a file picker, uploads
// to /api/admin/landing/upload-image, then writes the resulting URL back
// to the caller via `onUploaded(url)`. ─────────────────────────────────
const ImageField = ({ value, onChange, auth, label = "Immagine" }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", f);
      const r = await axios.post(`${API}/api/admin/landing/upload-image`, form, {
        headers: { ...auth, "Content-Type": "multipart/form-data" },
      });
      onChange(r.data?.url || "");
      toast.success("Immagine caricata");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload fallito");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };
  return (
    <div className="space-y-2">
      <p className="text-[10px] uppercase tracking-[0.2em] text-[#cfb53b]/60 font-heading">{label}</p>
      <div className="flex items-center gap-2">
        <div className="w-16 h-16 border border-[#2d3748] bg-[#0f1117] flex items-center justify-center overflow-hidden flex-shrink-0">
          {value ? (
            <img src={value} alt="" className="w-full h-full object-cover" />
          ) : (
            <ImageIcon className="text-[#475569]" size={20} />
          )}
        </div>
        <div className="flex-1 flex flex-col gap-1.5">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="px-3 py-1.5 bg-[#cfb53b]/15 text-[#cfb53b] border border-[#cfb53b]/30 rounded-sm text-xs inline-flex items-center gap-1.5 hover:bg-[#cfb53b]/25 disabled:opacity-50 w-fit"
          >
            {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
            {value ? "Cambia" : "Carica"}
          </button>
          {value && (
            <button type="button" onClick={() => onChange("")}
                    className="text-[10px] text-red-300/80 hover:text-red-300 w-fit">Rimuovi</button>
          )}
        </div>
        <input ref={inputRef} type="file" className="hidden" accept="image/png,image/jpeg,image/webp,image/gif" onChange={onPick} />
      </div>
    </div>
  );
};

// ─── Field renderers ──────────────────────────────────────────────────
const TextField = ({ label, value, onChange, multiline, placeholder }) => (
  <div className="space-y-1">
    <p className="text-[10px] uppercase tracking-[0.2em] text-[#cfb53b]/60 font-heading">{label}</p>
    {multiline ? (
      <textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={3}
        className="w-full bg-[#0f1117] border border-[#2d3748] focus:border-[#cfb53b]/50 outline-none text-[13px] text-[#e2e8f0] px-2.5 py-1.5 font-body resize-y"
      />
    ) : (
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-[#0f1117] border border-[#2d3748] focus:border-[#cfb53b]/50 outline-none text-[13px] text-[#e2e8f0] px-2.5 py-1.5 font-body"
      />
    )}
  </div>
);

// ─── Section-specific forms ───────────────────────────────────────────
const HeroForm = ({ content, onChange, auth }) => {
  const set = (k, v) => onChange({ ...content, [k]: v });
  return (
    <div className="space-y-3">
      <TextField label="Eyebrow" value={content.eyebrow} onChange={(v) => set("eyebrow", v)} placeholder="· L'ALBA DI UNA LEGGENDA ·" />
      <TextField label="Titolo" value={content.title} onChange={(v) => set("title", v)} placeholder="Arcane Chronicles" />
      <TextField label="Sottotitolo" value={content.subtitle} onChange={(v) => set("subtitle", v)} multiline />
      <div className="grid grid-cols-2 gap-3">
        <TextField label="CTA Primario — Testo" value={content.cta_primary_label} onChange={(v) => set("cta_primary_label", v)} />
        <TextField label="CTA Primario — Link" value={content.cta_primary_href} onChange={(v) => set("cta_primary_href", v)} placeholder="/auth" />
        <TextField label="CTA Secondario — Testo" value={content.cta_secondary_label} onChange={(v) => set("cta_secondary_label", v)} />
        <TextField label="CTA Secondario — Link" value={content.cta_secondary_href} onChange={(v) => set("cta_secondary_href", v)} placeholder="/hall" />
      </div>
      <ImageField label="Immagine di sfondo" value={content.background_image} onChange={(v) => set("background_image", v)} auth={auth} />
    </div>
  );
};

const SplitForm = ({ content, onChange, auth }) => {
  const set = (k, v) => onChange({ ...content, [k]: v });
  const setStat = (i, k, v) => {
    const stats = [...(content.stats || [])];
    stats[i] = { ...stats[i], [k]: v };
    set("stats", stats);
  };
  return (
    <div className="space-y-3">
      <TextField label="Eyebrow" value={content.eyebrow} onChange={(v) => set("eyebrow", v)} />
      <TextField label="Titolo" value={content.title} onChange={(v) => set("title", v)} />
      <TextField label="Sottotitolo (in oro)" value={content.subtitle} onChange={(v) => set("subtitle", v)} />
      <TextField label="Corpo" value={content.body} onChange={(v) => set("body", v)} multiline />
      <div>
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#cfb53b]/60 font-heading mb-2">Statistiche</p>
        {(content.stats || []).map((stat, i) => (
          <div key={i} className="flex gap-2 mb-2">
            <input type="text" placeholder="Valore" value={stat.value || ""} onChange={(e) => setStat(i, "value", e.target.value)}
                   className="flex-1 bg-[#0f1117] border border-[#2d3748] outline-none text-[13px] text-[#e2e8f0] px-2 py-1" />
            <input type="text" placeholder="Etichetta" value={stat.label || ""} onChange={(e) => setStat(i, "label", e.target.value)}
                   className="flex-1 bg-[#0f1117] border border-[#2d3748] outline-none text-[13px] text-[#e2e8f0] px-2 py-1" />
            <button type="button" onClick={() => set("stats", content.stats.filter((_, j) => j !== i))}
                    className="px-2 text-red-300 hover:text-red-400"><Trash2 size={14} /></button>
          </div>
        ))}
        <button type="button" onClick={() => set("stats", [...(content.stats || []), { value: "", label: "" }])}
                className="text-xs text-[#cfb53b]/70 hover:text-[#cfb53b] flex items-center gap-1">
          <Plus size={12} /> Aggiungi statistica
        </button>
      </div>
      <ImageField label="Immagine" value={content.image} onChange={(v) => set("image", v)} auth={auth} />
      <TextField label="Didascalia immagine" value={content.image_caption} onChange={(v) => set("image_caption", v)} />
      <div className="space-y-1">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#cfb53b]/60 font-heading">Posizione immagine</p>
        <select value={content.image_position || "right"} onChange={(e) => set("image_position", e.target.value)}
                className="w-full bg-[#0f1117] border border-[#2d3748] outline-none text-[13px] text-[#e2e8f0] px-2.5 py-1.5">
          <option value="right">Destra</option>
          <option value="left">Sinistra</option>
        </select>
      </div>
    </div>
  );
};

const FeatureGridForm = ({ content, onChange, auth }) => {
  const set = (k, v) => onChange({ ...content, [k]: v });
  const setCard = (i, k, v) => {
    const cards = [...(content.cards || [])];
    cards[i] = { ...cards[i], [k]: v };
    set("cards", cards);
  };
  return (
    <div className="space-y-3">
      <TextField label="Eyebrow" value={content.eyebrow} onChange={(v) => set("eyebrow", v)} />
      <TextField label="Titolo" value={content.title} onChange={(v) => set("title", v)} multiline placeholder="Usa \n per andare a capo" />
      <TextField label="Corpo" value={content.body} onChange={(v) => set("body", v)} multiline />
      <div>
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#cfb53b]/60 font-heading mb-2">Card</p>
        {(content.cards || []).map((card, i) => (
          <div key={i} className="border border-[#2d3748] p-3 mb-2 space-y-2 bg-[#0a0a0c]/50">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-heading text-[#cfb53b]/80">Card {i + 1}</p>
              <button type="button" onClick={() => set("cards", content.cards.filter((_, j) => j !== i))}
                      className="text-red-300 hover:text-red-400"><Trash2 size={12} /></button>
            </div>
            <select value={card.icon || "sparkles"} onChange={(e) => setCard(i, "icon", e.target.value)}
                    className="w-full bg-[#0f1117] border border-[#2d3748] outline-none text-[12px] text-[#e2e8f0] px-2 py-1">
              {ICON_OPTIONS.map(opt => React.createElement("option", { key: opt, value: opt }, opt))}
            </select>
            <input type="text" placeholder="Titolo" value={card.title || ""} onChange={(e) => setCard(i, "title", e.target.value)}
                   className="w-full bg-[#0f1117] border border-[#2d3748] outline-none text-[13px] text-[#e2e8f0] px-2 py-1" />
            <textarea placeholder="Body" value={card.body || ""} onChange={(e) => setCard(i, "body", e.target.value)} rows={2}
                      className="w-full bg-[#0f1117] border border-[#2d3748] outline-none text-[13px] text-[#e2e8f0] px-2 py-1 resize-y" />
          </div>
        ))}
        <button type="button" onClick={() => set("cards", [...(content.cards || []), { icon: "sparkles", title: "", body: "" }])}
                className="text-xs text-[#cfb53b]/70 hover:text-[#cfb53b] flex items-center gap-1">
          <Plus size={12} /> Aggiungi card
        </button>
      </div>
      <ImageField label="Immagine sotto le card (opz.)" value={content.image} onChange={(v) => set("image", v)} auth={auth} />
      <TextField label="Didascalia immagine" value={content.image_caption} onChange={(v) => set("image_caption", v)} />
    </div>
  );
};

const CtaForm = ({ content, onChange, auth }) => {
  const set = (k, v) => onChange({ ...content, [k]: v });
  return (
    <div className="space-y-3">
      <TextField label="Titolo" value={content.title} onChange={(v) => set("title", v)} multiline />
      <TextField label="Body" value={content.body} onChange={(v) => set("body", v)} multiline />
      <div className="grid grid-cols-2 gap-3">
        <TextField label="Bottone — Testo" value={content.button_label} onChange={(v) => set("button_label", v)} />
        <TextField label="Bottone — Link" value={content.button_href} onChange={(v) => set("button_href", v)} placeholder="/auth" />
      </div>
      <ImageField label="Immagine di sfondo" value={content.background_image} onChange={(v) => set("background_image", v)} auth={auth} />
    </div>
  );
};

const FORM_MAP = { hero: HeroForm, split: SplitForm, feature_grid: FeatureGridForm, cta: CtaForm };

// ─── Main editor ───────────────────────────────────────────────────────
export default function LandingEditorTab({ token }) {
  const [sections, setSections] = useState([]);
  const [publishedAt, setPublishedAt] = useState(null);
  const [draftUpdatedAt, setDraftUpdatedAt] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [editorLang, setEditorLang] = useState("it");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [dirty, setDirty] = useState(false);
  // UI state for the "Add section" picker
  const [showAddPicker, setShowAddPicker] = useState(false);
  const [addAtIndex, setAddAtIndex] = useState(null); // null = append to end
  const iframeRef = useRef(null);
  const [previewRefreshKey, setPreviewRefreshKey] = useState(0);

  const auth = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchDraft = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/landing/draft`, { headers: auth });
      const draftSections = r.data?.draft?.sections || [];
      setSections(draftSections);
      setPublishedAt(r.data?.published_at || null);
      setDraftUpdatedAt(r.data?.draft_updated_at || null);
      // Initialize selection on first load only (without retriggering this effect)
      setSelectedId(prev => prev || draftSections[0]?.id || null);
      setDirty(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore caricamento bozza");
    } finally {
      setLoading(false);
    }
  }, [auth]);

  useEffect(() => { fetchDraft(); }, [fetchDraft]);

  // Warn user before they navigate away with unsaved changes
  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const selected = sections.find(s => s.id === selectedId);

  const updateSection = (id, patch) => {
    setSections(sections.map(s => s.id === id ? { ...s, ...patch } : s));
    setDirty(true);
  };

  const updateContent = (id, lang, content) => {
    setSections(sections.map(s => s.id === id
      ? { ...s, content: { ...s.content, [lang]: content } }
      : s,
    ));
    setDirty(true);
  };

  const addSection = (type, atIdx = null) => {
    const newOne = newSection(type);
    if (atIdx === null) {
      // Append to end
      setSections([...sections, newOne]);
    } else {
      // Insert at index
      const next = [...sections.slice(0, atIdx), newOne, ...sections.slice(atIdx)];
      setSections(next);
    }
    setSelectedId(newOne.id);
    setDirty(true);
    setShowAddPicker(false);
    setAddAtIndex(null);
  };

  const deleteSection = (id) => {
    if (!window.confirm("Eliminare questa sezione?")) return;
    const idx = sections.findIndex(s => s.id === id);
    const next = sections.filter(s => s.id !== id);
    setSections(next);
    if (selectedId === id && next.length > 0) {
      setSelectedId(next[Math.max(0, idx - 1)].id);
    } else if (next.length === 0) {
      setSelectedId(null);
    }
    setDirty(true);
  };

  const duplicateSection = (id) => {
    const src = sections.find(s => s.id === id);
    if (!src) return;
    const dupe = { ...clone(src), id: crypto.randomUUID() };
    const idx = sections.findIndex(s => s.id === id);
    const next = [...sections.slice(0, idx + 1), dupe, ...sections.slice(idx + 1)];
    setSections(next);
    setSelectedId(dupe.id);
    setDirty(true);
  };

  const moveSection = (id, direction) => {
    const idx = sections.findIndex(s => s.id === id);
    if (idx < 0) return;
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= sections.length) return;
    const next = [...sections];
    [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
    setSections(next);
    setDirty(true);
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/api/admin/landing/draft`, { sections }, { headers: auth });
      setDraftUpdatedAt(r.data?.draft_updated_at || null);
      setDirty(false);
      toast.success("Bozza salvata");
      // Refresh preview iframe
      setPreviewRefreshKey(k => k + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Salvataggio fallito");
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    if (dirty && !window.confirm("Hai modifiche non salvate. Pubblicare la bozza già salvata? (Le modifiche correnti non saranno incluse)")) return;
    if (!window.confirm("Pubblicare la bozza? Sarà immediatamente visibile a tutti.")) return;
    setPublishing(true);
    try {
      const r = await axios.post(`${API}/api/admin/landing/publish`, null, { headers: auth });
      setPublishedAt(r.data?.published_at || null);
      toast.success("Pubblicato live");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Pubblicazione fallita");
    } finally {
      setPublishing(false);
    }
  };

  const cleanupStaleUrls = async () => {
    if (!window.confirm("Rimuovere gli URL immagini obsoleti (da deploy precedenti)? Dovrai ri-caricare le immagini interessate.")) return;
    try {
      const r = await axios.post(`${API}/api/admin/landing/cleanup-stale-urls`, null, { headers: auth });
      const n = r.data?.n_cleared || 0;
      if (n === 0) {
        toast.success("Nessun URL obsoleto trovato");
      } else {
        toast.success(`${n} URL obsoleti puliti. Ri-carica le immagini.`);
      }
      await fetchDraft();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Pulizia fallita");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-[#94a3b8] text-sm">
        <Loader2 className="animate-spin mr-2" size={16} /> Caricamento editor…
      </div>
    );
  }

  const SectionForm = selected ? FORM_MAP[selected.type] : null;
  const previewUrl = `/?draft=1&_=${previewRefreshKey}`;

  return (
    <div className="space-y-3" data-testid="landing-editor-panel">
      {/* Top bar with stats + actions */}
      <div className="bg-[#1a1a1d]/80 border border-[#cfb53b]/15 rounded-md p-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-4 text-xs text-[#94a3b8] font-body">
          <span>📝 {sections.length} sezioni</span>
          {draftUpdatedAt && <span>Bozza: {new Date(draftUpdatedAt).toLocaleString("it")}</span>}
          {publishedAt && <span className="text-emerald-400/80">Live: {new Date(publishedAt).toLocaleString("it")}</span>}
          {dirty && <span className="text-amber-400">● modifiche non salvate</span>}
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={cleanupStaleUrls} disabled={saving || publishing}
                  className="px-3 py-1.5 text-xs text-amber-300/80 hover:text-amber-300 inline-flex items-center gap-1 disabled:opacity-50"
                  title="Rimuovi URL immagini obsoleti (es. da prima del fix Cloudinary)"
                  data-testid="landing-cleanup-stale">
            <Trash2 size={12} /> Pulisci URL obsoleti
          </button>
          <button type="button" onClick={fetchDraft} disabled={saving || publishing}
                  className="px-3 py-1.5 text-xs text-[#94a3b8] hover:text-[#cfb53b] inline-flex items-center gap-1 disabled:opacity-50"
                  data-testid="landing-refresh">
            <RefreshCw size={12} /> Ricarica
          </button>
          <button type="button" onClick={saveDraft} disabled={saving || publishing}
                  className="px-3 py-1.5 bg-[#cfb53b]/15 text-[#cfb53b] border border-[#cfb53b]/30 rounded-sm text-xs inline-flex items-center gap-1.5 hover:bg-[#cfb53b]/25 disabled:opacity-50"
                  data-testid="landing-save-draft">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
            Salva bozza
          </button>
          <button type="button" onClick={publish} disabled={publishing || saving}
                  className="px-4 py-1.5 bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 rounded-sm text-xs inline-flex items-center gap-1.5 hover:bg-emerald-500/30 disabled:opacity-50"
                  data-testid="landing-publish">
            {publishing ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} />}
            Pubblica
          </button>
        </div>
      </div>

      {/* Split: editor (left) + preview iframe (right) */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-3">
        {/* LEFT: Sections list + form */}
        <div className="xl:col-span-7 space-y-3">
          {/* Sections list */}
          <div className="bg-[#1a1a1d]/60 border border-[#2d3748] rounded-md p-3">
            <div className="flex items-center justify-between gap-2 mb-3">
              <p className="text-xs uppercase tracking-[0.2em] text-[#cfb53b]/70 font-heading">
                Sezioni ({sections.length})
              </p>
              <button
                type="button"
                onClick={() => { setAddAtIndex(null); setShowAddPicker(true); }}
                className="px-3 py-1.5 bg-[#cfb53b] text-[#0a0a0c] rounded-sm text-xs inline-flex items-center gap-1.5 font-heading uppercase tracking-wider hover:bg-[#e5cc5b] transition-colors"
                data-testid="landing-add-section">
                <Plus size={12} /> Aggiungi sezione
              </button>
            </div>
            {sections.length === 0 ? (
              <div className="text-center py-8 text-[#64748b]">
                <p className="text-xs italic mb-3">Nessuna sezione presente.</p>
                <button
                  type="button"
                  onClick={() => { setAddAtIndex(0); setShowAddPicker(true); }}
                  className="px-4 py-2 bg-[#cfb53b]/15 text-[#cfb53b] border border-[#cfb53b]/30 rounded-sm text-xs inline-flex items-center gap-1.5 hover:bg-[#cfb53b]/25"
                >
                  <Plus size={12} /> Crea la prima sezione
                </button>
              </div>
            ) : (
              <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
                {sections.map((s, i) => (
                  <React.Fragment key={s.id}>
                    {/* Insert-between button (shows on hover of the gap) */}
                    {i > 0 && (
                      <div className="relative h-0 flex items-center justify-center group/insert">
                        <button
                          type="button"
                          onClick={() => { setAddAtIndex(i); setShowAddPicker(true); }}
                          className="absolute opacity-0 group-hover/insert:opacity-100 transition-opacity bg-[#cfb53b]/20 hover:bg-[#cfb53b]/40 text-[#cfb53b] rounded-full p-0.5 z-10"
                          title="Inserisci una nuova sezione qui"
                          data-testid={`landing-insert-at-${i}`}>
                          <Plus size={10} />
                        </button>
                      </div>
                    )}
                    <div
                       className={`flex items-center gap-1.5 px-2 py-1.5 border rounded-sm text-xs cursor-pointer transition-colors ${
                         selectedId === s.id
                           ? "bg-[#cfb53b]/10 border-[#cfb53b]/40"
                           : "bg-[#0f1117]/80 border-[#2d3748] hover:border-[#cfb53b]/20"
                       }`}
                       onClick={() => setSelectedId(s.id)}
                       data-testid={`landing-section-row-${i}`}>
                      <span className="text-[#64748b] font-mono text-[10px] w-5">{i + 1}.</span>
                      <span className="text-[#cfb53b]/90 font-heading uppercase tracking-wide text-[10px] w-24 truncate">{s.type}</span>
                      <span className="flex-1 text-[#e2e8f0] truncate font-body">
                        {s.content?.it?.title || s.content?.en?.title || s.content?.it?.eyebrow || "(senza titolo)"}
                      </span>
                      <button type="button" onClick={(e) => { e.stopPropagation(); updateSection(s.id, { visible: !s.visible }); }}
                              className={`p-1 rounded ${s.visible ? "text-emerald-300/80 hover:bg-emerald-500/10" : "text-[#475569] hover:bg-[#475569]/10"}`}
                              title={s.visible ? "Visibile" : "Nascosta"}>
                        {s.visible ? <Eye size={11} /> : <EyeOff size={11} />}
                      </button>
                      <button type="button" onClick={(e) => { e.stopPropagation(); moveSection(s.id, -1); }} disabled={i === 0}
                              className="p-1 text-[#64748b] hover:text-[#cfb53b] disabled:opacity-30"
                              data-testid={`landing-section-up-${i}`}>
                        <ChevronUp size={11} />
                      </button>
                      <button type="button" onClick={(e) => { e.stopPropagation(); moveSection(s.id, 1); }} disabled={i === sections.length - 1}
                              className="p-1 text-[#64748b] hover:text-[#cfb53b] disabled:opacity-30"
                              data-testid={`landing-section-down-${i}`}>
                        <ChevronDown size={11} />
                      </button>
                      <button type="button" onClick={(e) => { e.stopPropagation(); duplicateSection(s.id); }}
                              className="p-1 text-[#64748b] hover:text-[#cfb53b]" title="Duplica">
                        <Copy size={11} />
                      </button>
                      <button type="button" onClick={(e) => { e.stopPropagation(); deleteSection(s.id); }}
                              className="p-1 text-red-300/70 hover:text-red-300"
                              data-testid={`landing-section-delete-${i}`}>
                        <Trash2 size={11} />
                      </button>
                    </div>
                  </React.Fragment>
                ))}
                {/* Append-at-end button */}
                <button
                  type="button"
                  onClick={() => { setAddAtIndex(null); setShowAddPicker(true); }}
                  className="w-full mt-2 px-3 py-2 border-2 border-dashed border-[#cfb53b]/20 hover:border-[#cfb53b]/50 hover:bg-[#cfb53b]/5 text-[#cfb53b]/60 hover:text-[#cfb53b] rounded-sm text-xs inline-flex items-center justify-center gap-1.5 transition-all"
                  data-testid="landing-add-section-end">
                  <Plus size={12} /> Aggiungi sezione in fondo
                </button>
              </div>
            )}
          </div>

          {/* Form */}
          {selected && SectionForm && (
            <div className="bg-[#1a1a1d]/60 border border-[#2d3748] rounded-md p-4 space-y-3">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs uppercase tracking-[0.2em] text-[#cfb53b]/70 font-heading">
                  Modifica · {SECTION_TYPE_LABELS[selected.type]}
                </p>
                <div className="flex items-center gap-1 text-xs">
                  <Globe2 size={12} className="text-[#cfb53b]/60" />
                  {LANGS.map(l => (
                    <button key={l.id} type="button" onClick={() => setEditorLang(l.id)}
                            className={`px-2 py-0.5 rounded-sm font-heading text-[10px] uppercase tracking-wider transition-colors ${
                              editorLang === l.id ? "bg-[#cfb53b]/20 text-[#cfb53b]" : "text-[#64748b] hover:text-[#cfb53b]/70"
                            }`}
                            data-testid={`landing-lang-tab-${l.id}`}>
                      {l.id}
                    </button>
                  ))}
                </div>
              </div>
              <SectionForm
                content={selected.content?.[editorLang] || {}}
                onChange={(c) => updateContent(selected.id, editorLang, c)}
                auth={auth}
              />
            </div>
          )}
        </div>

        {/* RIGHT: Live preview iframe */}
        <div className="xl:col-span-5">
          <div className="bg-[#1a1a1d]/60 border border-[#2d3748] rounded-md p-3 sticky top-4">
            <div className="flex items-center justify-between gap-2 mb-2">
              <p className="text-xs uppercase tracking-[0.2em] text-[#cfb53b]/70 font-heading">Anteprima bozza</p>
              <button type="button" onClick={() => setPreviewRefreshKey(k => k + 1)}
                      className="text-[10px] px-2 py-1 text-[#94a3b8] hover:text-[#cfb53b] inline-flex items-center gap-1"
                      data-testid="landing-preview-refresh">
                <RefreshCw size={11} /> Ricarica
              </button>
            </div>
            <iframe
              ref={iframeRef}
              src={previewUrl}
              title="Landing draft preview"
              className="w-full bg-[#0a0a0c] border border-[#2d3748] rounded-sm"
              style={{ height: "75vh" }}
              data-testid="landing-preview-iframe"
            />
            <p className="text-[10px] text-[#64748b] mt-2 italic">
              Salva la bozza per aggiornare l'anteprima.
            </p>
          </div>
        </div>
      </div>

      {/* Iter 92.3 — Add section picker modal */}
      {showAddPicker && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeIn"
          onClick={() => setShowAddPicker(false)}
          data-testid="landing-add-picker"
        >
          <div
            className="bg-[#15151a] border border-[#cfb53b]/30 rounded-md p-6 max-w-2xl w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-[#cfb53b]/70 font-heading">Scegli il tipo di sezione</p>
                <p className="text-[11px] text-[#94a3b8] mt-1 font-body">
                  {addAtIndex === null
                    ? "La nuova sezione sarà aggiunta in fondo alla pagina."
                    : `La nuova sezione sarà inserita prima della posizione ${addAtIndex + 1}.`}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowAddPicker(false)}
                className="text-[#94a3b8] hover:text-[#cfb53b] p-1"
                data-testid="landing-picker-close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {SECTION_TYPES.map(t => (
                <button
                  key={t}
                  type="button"
                  onClick={() => addSection(t, addAtIndex)}
                  className="text-left p-4 bg-[#0f1117] border border-[#2d3748] rounded-sm hover:border-[#cfb53b]/50 hover:bg-[#cfb53b]/5 transition-all group"
                  data-testid={`landing-picker-${t}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Plus size={14} className="text-[#cfb53b]/70 group-hover:text-[#cfb53b]" />
                    <p className="font-heading text-sm text-[#cfb53b]">{SECTION_TYPE_LABELS[t]}</p>
                  </div>
                  <p className="text-[11px] text-[#cbd5e1]/70 font-body leading-relaxed">{SECTION_TYPE_DESCRIPTIONS[t]}</p>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
